import { CRM_ORIGIN } from "@/lib/shopApi";

/**
 * THE single helper for localized product text.
 * Product translations come from the CRM (`vertalingen`), never from the client.
 * Falls back to the Dutch base field (naam / omschrijving) — briefing §6.3.
 */
/**
 * Productteksten per taal uit het CRM. Het CRM levert `vertalingen` met taalnamen
 * ("nederlands") of taalcodes ("nl") als sleutel; beide worden ondersteund.
 */
const TAAL_SLEUTELS = {
  nl: ["nl", "nl-nl", "nederlands", "dutch"],
  en: ["en", "en-gb", "en-us", "engels", "english"],
  de: ["de", "de-de", "duits", "deutsch", "german"],
  fr: ["fr", "fr-fr", "frans", "francais", "français", "french"],
};

export function productText(product, field, lang) {
  const vertalingen = product?.vertalingen;
  if (!vertalingen || typeof vertalingen !== "object") return "";
  const code = String(lang || "nl").split("-")[0].toLowerCase();
  const sleutels = TAAL_SLEUTELS[code] || [code];
  const genormaliseerd = Object.fromEntries(
    Object.entries(vertalingen).map(([key, waarde]) => [String(key).trim().toLowerCase(), waarde]),
  );
  for (const sleutel of sleutels) {
    const waarde = genormaliseerd[sleutel]?.[field];
    if (typeof waarde === "string" && waarde.trim() !== "") return waarde.trim();
  }
  return "";
}

/**
 * Lijstveld uit het CRM (usp, claims): array of tekst gescheiden door regels, ';', '•' of '|'.
 * Vertaald via vertalingen.<taal>.<veld>, anders het Nederlandse basisveld.
 */
export function productList(product, field, lang) {
  const code = String(lang || "nl").split("-")[0].toLowerCase();
  const vertalingen = Object.fromEntries(
    Object.entries(product?.vertalingen || {}).map(([k, v]) => [String(k).trim().toLowerCase(), v]),
  );
  const vertaald = (TAAL_SLEUTELS[code] || [code]).map((k) => vertalingen[k]?.[field]).find((v) => (Array.isArray(v) ? v.length : typeof v === "string" && v.trim()));
  const ruw = vertaald || product?.[field];
  const items = Array.isArray(ruw) ? ruw : String(ruw || "").split(/\r?\n|;|•|\||,/);
  return items.map((x) => String(x ?? "").replace(/^[-–*\s]+/, "").trim()).filter(Boolean);
}

/**
 * Tekst voor de gekozen taal. Namen vallen terug op de CRM-productnaam; teksten
 * zoals de omschrijving komen uitsluitend uit de productteksten per taal
 * (staat die niet in het CRM, dan tonen we niets).
 */
export function getLocalizedField(product, field, lang) {
  if (!product) return "";
  const perTaal = productText(product, field, lang);
  if (perTaal) return perTaal;
  if (field === "naam") return product.naam || "";
  return "";
}


/** Prefix the relative CRM image path with the CRM origin (without /api). */
export function productImageUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${CRM_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

/** Verkleinde, gecachte variant via de eigen backend (CRM-originelen zijn vaak >3 MB). */
export function thumbUrl(src, w = 480, { alpha = false, trim = false } = {}) {
  if (!src) return src;
  const isCrm = src.startsWith(`${CRM_ORIGIN}/api/producten/`) || src.startsWith("/api/producten/");
  if (!isCrm) return src;
  // Retina-schermen vragen om meer pixels; cap op 1.5x houdt het scherp én licht.
  const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 1.5) : 1;
  const breedte = Math.round(w * dpr);
  return `${process.env.REACT_APP_BACKEND_URL}/api/afbeelding?src=${encodeURIComponent(src)}&w=${breedte}${alpha ? "&alpha=1" : ""}${trim ? "&trim=1" : ""}`;
}

export function productImages(product) {
  if (!product) return [];
  const list = [];
  if (product.hoofdfoto) list.push(product.hoofdfoto);
  (product.afbeeldingen || []).forEach((a) => {
    if (a && !list.includes(a)) list.push(a);
  });
  return list.map(productImageUrl).filter(Boolean);
}

const LOCALE_BY_LANG = { nl: "nl-NL", en: "en-GB", de: "de-DE", fr: "fr-FR" };

export function formatCurrency(value, lang = "nl") {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(LOCALE_BY_LANG[lang] || "nl-NL", {
    style: "currency",
    currency: "EUR",
  }).format(n);
}

export function formatNumber(value, lang = "nl") {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat(LOCALE_BY_LANG[lang] || "nl-NL", {
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatDate(value, lang = "nl") {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return new Intl.DateTimeFormat(LOCALE_BY_LANG[lang] || "nl-NL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

/**
 * Vaste BTW-tarieven per land — UITSLUITEND voor de margeberekening.
 * Facturatie en de weergegeven prijzen lopen altijd via het CRM (`btw_tarief`).
 */
export const VAT_BY_COUNTRY = { nl: 21, be: 21, de: 19, fr: 20 };
export const DEFAULT_COUNTRY = "nl";

const COUNTRY_ALIASES = {
  nl: "nl", nld: "nl", nederland: "nl", netherlands: "nl", holland: "nl", "pays-bas": "nl", niederlande: "nl",
  be: "be", bel: "be", belgie: "be", "belgië": "be", belgium: "be", belgique: "be", belgien: "be",
  de: "de", deu: "de", duitsland: "de", deutschland: "de", germany: "de", allemagne: "de",
  fr: "fr", fra: "fr", frankrijk: "fr", france: "fr", frankreich: "fr",
};

/**
 * Landcode (nl/be/de/fr) van het ingelogde account. Het door de klant ingevulde
 * bezorgadres is leidend (ook direct na een wijziging), daarna het land van de
 * organisatie en pas als laatste de CRM-regio.
 */
export function accountCountry(account) {
  const org = account?.organisatie;
  const bronnen = [
    org?.bezorgadres?.land,
    org?.land,
    org?.factuuradres?.land,
    org?.regio,
    account?.land,
    account?.regio,
  ];
  for (const bron of bronnen) {
    if (typeof bron !== "string") continue;
    const code = COUNTRY_ALIASES[bron.trim().toLowerCase()];
    if (code) return code;
  }
  return DEFAULT_COUNTRY;
}

/** Klantnummer uit het CRM (klantcode/debiteurnummer). Null = nog niet geleverd. */
export function customerNumber(account) {
  const org = account?.organisatie || {};
  const kandidaten = [
    org.klantnummer,
    org.klantcode,
    org.debiteurnummer,
    org.relatienummer,
    account?.klantnummer,
    account?.klantcode,
  ];
  for (const waarde of kandidaten) {
    if (waarde == null) continue;
    const tekst = String(waarde).trim();
    if (tekst) return tekst;
  }
  return null;
}

/** Binnenlandse levering (NL) of intracommunautair (BE/DE/FR: BTW verlegd). */
export function isVatExempt(account) {
  const regime = String(account?.organisatie?.btw_regime || "").trim().toLowerCase();
  if (regime) return !regime.includes("binnenland");
  return accountCountry(account) !== "nl";
}

/**
 * BTW-percentage voor de weergave: 0 bij een intracommunautaire levering,
 * anders het landspecifieke tarief uit het CRM (`btw_pct_lokaal`), dan
 * `btw_tarief`, en als het CRM 0 doorgeeft het vaste tarief van het land.
 */
export function displayVatRate(product, account) {
  if (isVatExempt(account)) return 0;
  const lokaal = Number(product?.btw_pct_lokaal);
  if (Number.isFinite(lokaal) && lokaal > 0) return lokaal;
  const crm = Number(product?.btw_tarief);
  if (Number.isFinite(crm) && crm > 0) return crm;
  return VAT_BY_COUNTRY[accountCountry(account)] ?? VAT_BY_COUNTRY[DEFAULT_COUNTRY];
}

/** BTW-percentage voor de margeberekening op basis van het land van de klant. */
export function marginVatRate(account) {
  return VAT_BY_COUNTRY[accountCountry(account)] ?? VAT_BY_COUNTRY[DEFAULT_COUNTRY];
}

/**
 * Marge voor de koper: (adviesprijs excl. BTW − jouw prijs) / adviesprijs excl. BTW.
 * Geeft null als er geen bruikbare adviesprijs of inkoopprijs is.
 */
export function marginInfo(product, account) {
  const advies = Number(product?.consument_adviesprijs);
  const prijs = Number(product?.verkoopprijs);
  if (!Number.isFinite(advies) || advies <= 0) return null;
  if (!Number.isFinite(prijs) || prijs <= 0) return null;
  const btw = marginVatRate(account);
  const adviesExcl = advies / (1 + btw / 100);
  if (adviesExcl <= prijs) return null;
  return {
    percentage: Math.round(((adviesExcl - prijs) / adviesExcl) * 100),
    adviesExcl,
    advies,
    prijs,
    btw,
  };
}

/**
 * Levert het CRM dit product voor het land van deze klant?
 * `land_actief` is leidend: staat het land aan, dan mag het product daar verkocht
 * worden (ook als de regiovlag uit staat); staat het uit, dan niet. Levert het CRM
 * `land_actief` niet, dan geldt de verkoopvlag van de regio waarin de klant valt.
 */
export function isDeliverableInCountry(product, account) {
  if (product?.land_actief === true) return true;
  if (product?.land_actief === false) return false;
  const land = accountCountry(account);
  const vlag = land === "nl" ? product?.verkoop_nl : product?.verkoop_eu;
  return vlag !== false;
}

/**
 * Adviesprijs (UVP) uit het CRM — incl. BTW, alleen informatief (briefing feb-2026 §2a).
 * Geeft null terug bij 0 (= niet ingesteld) of wanneer die niet hoger is dan de contractprijs.
 */
export function recommendedPrice(product) {
  const advies = Number(product?.consument_adviesprijs);
  const prijs = Number(product?.verkoopprijs);
  if (!Number.isFinite(advies) || advies <= 0) return null;
  if (Number.isFinite(prijs) && advies <= prijs) return null;
  return advies;
}

/** Verkoop-toggle uit het CRM: false = tijdelijk niet leverbaar (briefing feb-2026 §2b). */
/** Eigen voorraad op? (op_voorraad false of voorraad <= 0) */
function eigenVoorraadOp(product) {
  if (product?.op_voorraad === false) return true;
  const voorraad = Number(product?.voorraad);
  return Number.isFinite(voorraad) && voorraad <= 0;
}

/** Niet op eigen voorraad, maar wél bij de leverancier: bestelbaar met levertijd. */
export function isSupplierBackorder(product) {
  return isAvailable(product) && eigenVoorraadOp(product) && product?.leverancier_op_voorraad === true;
}

export const SUPPLIER_LEAD_DAYS = 5;

/** Verwachte leverdatum: vandaag + n werkdagen (weekend telt niet mee). */
export function expectedDeliveryDate(days = SUPPLIER_LEAD_DAYS, from = new Date()) {
  const d = new Date(from);
  let resterend = days;
  while (resterend > 0) {
    d.setDate(d.getDate() + 1);
    if (d.getDay() !== 0 && d.getDay() !== 6) resterend -= 1;
  }
  return d;
}

export function formatDeliveryDate(date, lang) {
  return new Intl.DateTimeFormat(LOCALE_BY_LANG[lang] || "nl-NL", { weekday: "long", day: "numeric", month: "long" }).format(date);
}

/** Te koop? `beschikbaar` false = nee; eigen voorraad op én CRM zegt expliciet niet bij leverancier = nee. */
export function isAvailable(product) {
  if (product?.beschikbaar === false) return false;
  if (eigenVoorraadOp(product) && product?.leverancier_op_voorraad === false) return false;
  return true;
}

/** Mag dit product besteld worden? Niet-beschikbaar, prijsloos of niet in jouw land = nee. */
export function isOrderable(product, account) {
  return isAvailable(product) && isDeliverableInCountry(product, account) && Number(product?.verkoopprijs) > 0;
}

/** green / orange / red / grijs per briefing §4 + feb-2026 §2b */
export function stockLevel(product) {
  if (!product) return "out";
  if (!isAvailable(product)) return "unavailable";
  if (eigenVoorraadOp(product)) return product.leverancier_op_voorraad === true ? "supplier" : "out";
  const voorraad = Number(product.voorraad);
  if (Number.isFinite(voorraad) && voorraad <= 10) return "limited";
  return "in";
}

/* ------------------- verpakking: besteleenheid & minimale afname (CRM) ------------------- */
const positief = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Aantal stuks per verpakking uit het CRM (commerciële data). Null = geen regel. */
export function packSize(product) {
  return positief(product?.besteleenheid) ?? positief(product?.verpakkingseenheid) ?? null;
}

/** Minimale bestelhoeveelheid uit het CRM. Null = geen regel. */
export function minOrder(product) {
  return positief(product?.minimale_afname) ?? positief(product?.minimale_bestelhoeveelheid) ?? null;
}

export function hasPackagingRules(product) {
  return packSize(product) !== null || minOrder(product) !== null;
}

/**
 * Eenheid uit het CRM (Nederlands) in de taal van de klant, met meervoud.
 * Onbekende eenheden (bijv. 'ml', '75 g') laten we ongewijzigd staan.
 */
const EENHEDEN = {
  stuk: { nl: ["stuk", "stuks"], en: ["piece", "pieces"], de: ["Stück", "Stück"], fr: ["pièce", "pièces"] },
  doos: { nl: ["doos", "dozen"], en: ["box", "boxes"], de: ["Karton", "Kartons"], fr: ["boîte", "boîtes"] },
  pak: { nl: ["pak", "pakken"], en: ["pack", "packs"], de: ["Packung", "Packungen"], fr: ["paquet", "paquets"] },
  set: { nl: ["set", "sets"], en: ["set", "sets"], de: ["Set", "Sets"], fr: ["set", "sets"] },
  fles: { nl: ["fles", "flessen"], en: ["bottle", "bottles"], de: ["Flasche", "Flaschen"], fr: ["flacon", "flacons"] },
  tube: { nl: ["tube", "tubes"], en: ["tube", "tubes"], de: ["Tube", "Tuben"], fr: ["tube", "tubes"] },
  rol: { nl: ["rol", "rollen"], en: ["roll", "rolls"], de: ["Rolle", "Rollen"], fr: ["rouleau", "rouleaux"] },
  paar: { nl: ["paar", "paar"], en: ["pair", "pairs"], de: ["Paar", "Paare"], fr: ["paire", "paires"] },
  zak: { nl: ["zak", "zakken"], en: ["bag", "bags"], de: ["Beutel", "Beutel"], fr: ["sachet", "sachets"] },
  ragers: { nl: ["rager", "ragers"], en: ["brush", "brushes"], de: ["Bürste", "Bürsten"], fr: ["brossette", "brossettes"] },
};

const EENHEID_ALIAS = {
  stuk: "stuk", stuks: "stuk", st: "stuk", pc: "stuk", pcs: "stuk",
  doos: "doos", dozen: "doos", box: "doos",
  pak: "pak", pakken: "pak", pakje: "pak",
  set: "set", sets: "set",
  fles: "fles", flessen: "fles",
  tube: "tube", tubes: "tube",
  rol: "rol", rollen: "rol",
  paar: "paar",
  zak: "zak", zakken: "zak", zakje: "zak",
  rager: "ragers", ragers: "ragers",
};

export function unitLabel(eenheid, aantal = 1, lang = "nl") {
  const basis = String(eenheid || "").trim();
  if (!basis) return basis;
  const code = String(lang || "nl").split("-")[0].toLowerCase();
  const sleutel = EENHEID_ALIAS[basis.toLowerCase()];
  if (sleutel) {
    const vormen = EENHEDEN[sleutel]?.[code] || EENHEDEN[sleutel]?.nl;
    if (vormen) return aantal > 1 ? vormen[1] : vormen[0];
  }
  return code === "nl" && aantal > 1 && !/s$/i.test(basis) ? `${basis}s` : basis;
}

/** Rondt naar boven af op een hele verpakking en nooit onder de minimale afname. */
export function normalizeQuantity(product, aantal) {
  const pack = packSize(product);
  const min = minOrder(product);
  let n = Number(String(aantal ?? "").replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) n = min ?? pack ?? 1;
  if (min && n < min) n = min;
  if (pack) n = Math.ceil((n - 1e-9) / pack) * pack;
  return Number(n.toFixed(2));
}

/**
 * Minimum bestelbedrag (excl. BTW). Het CRM is leidend: /shop/verzendregels of
 * organisatie.minimum_bestelbedrag. Zolang het CRM niets levert geldt de
 * tijdelijke instelling per land uit het beheer.
 */
export function minimumOrderAmount({ account, verzendregels, instellingen } = {}) {
  const crm =
    positief(verzendregels?.minimum_bestelbedrag) ??
    positief(account?.organisatie?.minimum_bestelbedrag);
  if (crm) return { bedrag: crm, bron: "crm" };
  const fallback = positief((instellingen?.minimum_bestelbedrag || {})[accountCountry(account)]);
  if (fallback) return { bedrag: fallback, bron: "beheer" };
  return null;
}

/**
 * Verzendkosten en 'gratis vanaf' — CRM leidend (verzendregels), anders de
 * tijdelijke instelling per land uit het beheer. Null = niets bekend, niets tonen.
 */
export function shippingInfo({ account, verzendregels, instellingen, totalExcl = 0 } = {}) {
  const land = accountCountry(account);
  const perLand = (veld) => positief((instellingen?.[veld] || {})[land]);
  const kosten =
    positief(verzendregels?.verzendkosten) ??
    positief(account?.organisatie?.verzendkosten) ??
    perLand("verzendkosten");
  const gratisVanaf =
    positief(verzendregels?.gratis_verzending_vanaf) ??
    positief(account?.organisatie?.gratis_verzending_vanaf) ??
    perLand("gratis_verzending_vanaf");
  if (!kosten && !gratisVanaf) return null;
  const gratis = Boolean(gratisVanaf) && totalExcl + 0.005 >= gratisVanaf;
  return {
    kosten: kosten || 0,
    gratisVanaf: gratisVanaf || null,
    gratis: gratis || !kosten,
    tekortVoorGratis: gratisVanaf && !gratis ? gratisVanaf - totalExcl : 0,
  };
}

/** Categorie uit het CRM (commerciële data: webshop_categorie). Leeg = null. */
export function productCategory(product) {
  const waarde = product?.webshop_categorie;
  return typeof waarde === "string" && waarde.trim() !== "" ? waarde.trim() : null;
}

/** Unieke categorieën, alfabetisch. Producten zonder categorie leveren geen naam op. */
export function productCategories(producten) {
  const namen = new Set();
  (producten || []).forEach((p) => {
    const categorie = productCategory(p);
    if (categorie) namen.add(categorie);
  });
  return [...namen].sort((a, b) => a.localeCompare(b, "nl"));
}

/** Groepen [{categorie, label, producten}] met 'Overige' altijd als laatste. */
export function groupByCategory(producten, overigeLabel = "Overige") {
  const groepen = new Map();
  (producten || []).forEach((p) => {
    const key = productCategory(p) || "__overige__";
    if (!groepen.has(key)) groepen.set(key, []);
    groepen.get(key).push(p);
  });
  const namen = [...groepen.keys()].filter((k) => k !== "__overige__").sort((a, b) => a.localeCompare(b, "nl"));
  const lijst = namen.map((naam) => ({ categorie: naam, label: naam, producten: groepen.get(naam) }));
  if (groepen.has("__overige__")) {
    lijst.push({ categorie: "__overige__", label: overigeLabel, producten: groepen.get("__overige__") });
  }
  return lijst;
}

/** Pick the localized text from a showcase.json entry, falling back to nl. */
export function getShowcaseText(entry, field, lang) {
  const value = entry?.[field];
  if (!value) return "";
  if (typeof value === "string") return value;
  return value[lang] || value.nl || Object.values(value)[0] || "";
}
